import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, X, Loader2, Check, ChevronLeft, GripVertical, ArrowDownAZ } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, sortableKeyboardCoordinates, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GroupColorPicker } from './GroupColorPicker';
import { DEFAULT_GROUP_COLOR, resolveGroupTextColor } from './groupColors';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

// Group icon (two stacked rectangles) — used across all Group features.
export const GroupIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 1024 1024" className={className} fill="currentColor" aria-hidden="true">
    <path d="M779.855 894h-675.71C80.906 894 62 875.159 62 852V364c0-23.159 18.906-42 42.145-42h675.71C803.094 322 822 340.841 822 364v488c0 23.159-18.906 42-42.145 42zM112 844h660V372H112v472z" />
    <path d="M894 731H781v-50h105V209H232v139h-50V201c0-23.159 18.841-42 42-42h670c23.159 0 42 18.841 42 42v488c0 23.159-18.841 42-42 42z" />
  </svg>
);

// Sortable group row — used inside the List view of GroupsManagerModal.
const SortableGroupRow = ({ group, testIdPrefix, editingGroup, editName, setEditName, editColor, setEditColor, saveRename, cancelRename, startRename, openAllocate, handleDelete, itemLabelPlural }) => {
  const itemCount = group.device_count ?? group.item_count ?? (group.device_ids || group.item_ids || []).length ?? 0;
  const groupColor = group.color || DEFAULT_GROUP_COLOR;
  const isEditing = editingGroup?.id === group.id;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group.id, disabled: isEditing });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 rounded-lg border border-border bg-background p-2"
      data-testid={`${testIdPrefix}-row-${group.id}`}
    >
      {!isEditing && (
        <button
          type="button"
          className="shrink-0 mt-1 cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground"
          aria-label="Drag to reorder group"
          data-testid={`${testIdPrefix}-drag-handle-${group.id}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
      )}
      <GroupIcon className="w-4 h-4 mt-1 shrink-0" style={{ color: groupColor }} />
      <div className="min-w-0 flex-1">
        {isEditing ? (
          <div className="space-y-2">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveRename();
                if (e.key === 'Escape') cancelRename();
              }}
              autoFocus
              maxLength={60}
              className="h-8"
              data-testid={`${testIdPrefix}-rename-input-${group.id}`}
            />
            <GroupColorPicker value={editColor} onChange={setEditColor} testIdPrefix={`${testIdPrefix}-edit-color-${group.id}`} />
            <div className="flex items-center justify-end gap-1">
              <Button size="sm" variant="ghost" onClick={cancelRename}><X className="w-3.5 h-3.5" /></Button>
              <Button size="sm" onClick={saveRename} data-testid={`${testIdPrefix}-rename-save-${group.id}`}>
                <Check className="w-3.5 h-3.5" /><span className="ml-1">Save</span>
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p
              className="text-sm font-semibold truncate"
              style={{ color: resolveGroupTextColor(groupColor) }}
              data-testid={`${testIdPrefix}-name-${group.id}`}
            >
              {group.name}
            </p>
            <p className="text-xs text-muted-foreground">{itemCount} {itemCount === 1 ? itemLabelPlural.replace(/s$/, '') : itemLabelPlural}</p>
          </>
        )}
      </div>
      {!isEditing && (
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="outline" onClick={() => openAllocate(group)} data-testid={`${testIdPrefix}-allocate-${group.id}`}>Allocate</Button>
          <Button size="sm" variant="ghost" onClick={() => startRename(group)} data-testid={`${testIdPrefix}-rename-${group.id}`} aria-label="Edit group">
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => handleDelete(group)} data-testid={`${testIdPrefix}-delete-${group.id}`} aria-label="Delete group">
            <Trash2 className="w-3.5 h-3.5 text-red-500" />
          </Button>
        </div>
      )}
    </li>
  );
};

// Generic, owner-scoped Groups manager modal.
// Used by Live Tracking Device Groups and Geofencing Zone/Place/Route Groups.
//
// Props:
//   open, onOpenChange       — Dialog controlled state
//   api                       — axios-like instance (api.get/post/put/delete)
//   apiBase                   — e.g. '/device-groups' or '/geofence-groups'
//   apiParams                 — optional query params object for GET (e.g. {group_type:'zone'} for geofence)
//   createPayloadExtra        — optional extra fields injected into POST (e.g. {group_type:'zone'})
//   items                     — array of allocatable items for the Allocate pane (devices/zones/places/routes)
//   itemLabel                 — fn(item) -> string for display name (default: item.name)
//   itemSubLabel              — optional fn(item) -> string for the small line under the name
//   itemIdField               — defaults to 'id'
//   itemGroupField            — the field on each item that stores its current group_id (e.g. 'zone_group')
//                                — required for geofence (group_id on item) flow
//   membershipMode            — 'group_devices' (group has device_ids[]) or 'item_group_id' (item has group_id field)
//   allocateEndpoints         — { add: 'add-devices'|'add-items', remove: 'remove-devices'|'remove-items' }
//   title                      — modal title (e.g. 'Manage Device Groups', 'Manage Zone Groups')
//   itemLabelPlural           — e.g. 'devices', 'zones', 'places', 'routes'
//   onSaved                    — called after any mutation so the parent can refresh its list
export const GroupsManagerModal = ({
  open,
  onOpenChange,
  api,
  apiBase,
  apiParams = {},
  createPayloadExtra = {},
  items = [],
  itemLabel = (it) => it.name || it.asset_name,
  itemSubLabel,
  itemIdField = 'id',
  itemGroupField,
  membershipMode = 'group_devices',
  allocateEndpoints = { add: 'add-devices', remove: 'remove-devices' },
  title = 'Manage Groups',
  itemLabelPlural = 'items',
  onSaved,
  testIdPrefix = 'groups-manager',
}) => {
  const [view, setView] = useState('list');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_GROUP_COLOR);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState(DEFAULT_GROUP_COLOR);
  const [allocatingGroup, setAllocatingGroup] = useState(null);
  const [allocateSearch, setAllocateSearch] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [savingAllocate, setSavingAllocate] = useState(false);

  // Force re-render on theme change so getGroupTextColor() picks up the right shade.
  const [, setThemeTick] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    const observer = new MutationObserver(() => setThemeTick((t) => t + 1));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [open]);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(apiBase, { params: apiParams });
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, [api, apiBase, JSON.stringify(apiParams)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      setView('list');
      setEditingGroup(null);
      setNewName('');
      setNewColor(DEFAULT_GROUP_COLOR);
      setShowCreateForm(false);
      setAllocatingGroup(null);
      setAllocateSearch('');
      fetchGroups();
    }
  }, [open, fetchGroups]);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    const name = newName.trim();
    if (!name) {
      toast.error('Group name is required');
      return;
    }
    setCreating(true);
    try {
      await api.post(apiBase, { name, color: newColor, ...createPayloadExtra });
      setNewName('');
      setNewColor(DEFAULT_GROUP_COLOR);
      setShowCreateForm(false);
      await fetchGroups();
      onSaved?.();
      toast.success(`Group "${name}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const startRename = (group) => {
    setEditingGroup(group);
    setEditName(group.name);
    setEditColor(group.color || DEFAULT_GROUP_COLOR);
  };

  const cancelRename = () => {
    setEditingGroup(null);
    setEditName('');
    setEditColor(DEFAULT_GROUP_COLOR);
  };

  const saveRename = async () => {
    if (!editingGroup) return;
    const name = editName.trim();
    if (!name) {
      cancelRename();
      return;
    }
    const noChange = name === editingGroup.name && (editColor || DEFAULT_GROUP_COLOR).toLowerCase() === (editingGroup.color || DEFAULT_GROUP_COLOR).toLowerCase();
    if (noChange) {
      cancelRename();
      return;
    }
    try {
      await api.put(`${apiBase}/${editingGroup.id}`, { name, color: editColor });
      cancelRename();
      await fetchGroups();
      onSaved?.();
      toast.success('Group updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update group');
    }
  };

  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(null);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);

  const handleDelete = (group) => setConfirmDeleteGroup(group);

  const doDelete = async () => {
    const group = confirmDeleteGroup;
    setConfirmDeleteGroup(null);
    if (!group) return;
    try {
      await api.delete(`${apiBase}/${group.id}`);
      await fetchGroups();
      onSaved?.();
      toast.success(`Group "${group.name}" deleted`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete group');
    }
  };

  const openAllocate = (group) => {
    setAllocatingGroup(group);
    // Determine initial selection based on the membership mode
    if (membershipMode === 'item_group_id' && itemGroupField) {
      const ids = items.filter((it) => it[itemGroupField] === group.id).map((it) => it[itemIdField]);
      setSelectedItemIds(new Set(ids));
    } else {
      // 'group_devices' — group has device_ids[] (or item_ids[])
      setSelectedItemIds(new Set(group.device_ids || group.item_ids || []));
    }
    setAllocateSearch('');
    setView('allocate');
  };

  const backToList = () => {
    setAllocatingGroup(null);
    setSelectedItemIds(new Set());
    setAllocateSearch('');
    setView('list');
  };

  // Build a map of item_id -> name of OTHER group it currently belongs to, so the modal can
  // warn the user when allocating an item that is already in another group of the same owner.
  const otherGroupByItem = useMemo(() => {
    const map = {};
    if (membershipMode === 'item_group_id' && itemGroupField) {
      const groupNameById = {};
      groups.forEach((g) => { groupNameById[g.id] = g.name; });
      items.forEach((it) => {
        const gid = it[itemGroupField];
        if (gid && (!allocatingGroup || gid !== allocatingGroup.id) && groupNameById[gid]) {
          map[it[itemIdField]] = groupNameById[gid];
        }
      });
    } else {
      groups.forEach((g) => {
        if (allocatingGroup && g.id === allocatingGroup.id) return;
        (g.device_ids || g.item_ids || []).forEach((iid) => { map[iid] = g.name; });
      });
    }
    return map;
  }, [groups, items, allocatingGroup, membershipMode, itemGroupField, itemIdField]);

  const filteredItems = useMemo(() => {
    const q = allocateSearch.trim().toLowerCase();
    const matchingItems = !q ? items : items.filter((it) => {
      const name = String(itemLabel(it) || '').toLowerCase();
      const sub = itemSubLabel ? String(itemSubLabel(it) || '').toLowerCase() : '';
      return name.includes(q) || sub.includes(q);
    });
    return [...matchingItems].sort((a, b) => {
      const nameA = String(itemLabel(a) || '').trim();
      const nameB = String(itemLabel(b) || '').trim();
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [items, allocateSearch, itemLabel, itemSubLabel]);

  const toggleItem = (itemId) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const handleSaveAllocate = async () => {
    if (!allocatingGroup) return;
    let original;
    if (membershipMode === 'item_group_id' && itemGroupField) {
      original = new Set(items.filter((it) => it[itemGroupField] === allocatingGroup.id).map((it) => it[itemIdField]));
    } else {
      original = new Set(allocatingGroup.device_ids || allocatingGroup.item_ids || []);
    }
    const selected = selectedItemIds;
    const toAdd = [...selected].filter((id) => !original.has(id));
    const toRemove = [...original].filter((id) => !selected.has(id));
    if (toAdd.length === 0 && toRemove.length === 0) {
      backToList();
      return;
    }
    setSavingAllocate(true);
    try {
      const idKey = membershipMode === 'item_group_id' ? 'item_ids' : 'device_ids';
      if (toAdd.length) {
        await api.post(`${apiBase}/${allocatingGroup.id}/${allocateEndpoints.add}`, { [idKey]: toAdd });
      }
      if (toRemove.length) {
        await api.post(`${apiBase}/${allocatingGroup.id}/${allocateEndpoints.remove}`, { [idKey]: toRemove });
      }
      await fetchGroups();
      onSaved?.();
      toast.success(`Updated "${allocatingGroup.name}": ${toAdd.length} added, ${toRemove.length} removed`);
      backToList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save allocation');
    } finally {
      setSavingAllocate(false);
    }
  };

  // Persist a reordered set of group IDs to the backend.
  const handleReorder = useCallback(async (orderedIds) => {
    // Optimistic update so the UI feels instant.
    setGroups((prev) => {
      const byId = new Map(prev.map((g) => [g.id, g]));
      return orderedIds.map((id, idx) => ({ ...(byId.get(id) || {}), display_order: idx })).filter((g) => g.id);
    });
    try {
      await api.post(`${apiBase}/reorder`, { ordered_ids: orderedIds });
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save group order');
      await fetchGroups();
    }
  }, [api, apiBase, fetchGroups, onSaved]);

  // Reset all groups in the current view back to alphabetical (A→Z) ordering.
  const handleResetOrder = useCallback(() => setConfirmResetOpen(true), []);

  const doResetOrder = useCallback(async () => {
    setConfirmResetOpen(false);
    try {
      const params = apiParams && Object.keys(apiParams).length ? { params: apiParams } : undefined;
      await api.post(`${apiBase}/reset-order`, null, params);
      await fetchGroups();
      onSaved?.();
      toast.success('Groups reset to A→Z');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset order');
    }
  }, [api, apiBase, apiParams, fetchGroups, onSaved]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = groups.findIndex((g) => g.id === active.id);
    const newIndex = groups.findIndex((g) => g.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(groups, oldIndex, newIndex);
    handleReorder(next.map((g) => g.id));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-lg w-[95vw] max-h-[85vh] flex flex-col ${view === 'allocate' ? 'h-[85vh]' : ''}`} data-testid={`${testIdPrefix}-modal`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <GroupIcon className="w-5 h-5 text-[#2563eb]" />
            {view === 'list' ? title : `Allocate ${itemLabelPlural} · ${allocatingGroup?.name || ''}`}
          </DialogTitle>
        </DialogHeader>

        {view === 'list' ? (
          <div className="flex flex-col min-h-0 flex-1 gap-3">
            {showCreateForm ? (
              <form onSubmit={handleCreate} className="space-y-2 rounded-lg border border-border bg-muted/30 p-3" data-testid={`${testIdPrefix}-create-form`}>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="New group name…"
                  maxLength={60}
                  disabled={creating}
                  autoFocus
                  data-testid={`${testIdPrefix}-create-input`}
                />
                <GroupColorPicker value={newColor} onChange={setNewColor} testIdPrefix={`${testIdPrefix}-create-color`} />
                <div className="flex items-center justify-end gap-2 pt-1">
                  <Button type="button" variant="outline" size="sm" onClick={() => { setShowCreateForm(false); setNewName(''); setNewColor(DEFAULT_GROUP_COLOR); }} disabled={creating}>
                    Cancel
                  </Button>
                  <Button type="submit" size="sm" disabled={creating || !newName.trim()} data-testid={`${testIdPrefix}-create-submit`}>
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    <span className="ml-1">Create</span>
                  </Button>
                </div>
              </form>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="outline" className="flex-1 justify-start gap-2" onClick={() => setShowCreateForm(true)} data-testid={`${testIdPrefix}-show-create`}>
                  <Plus className="w-4 h-4" /> Create new group
                </Button>
                {groups.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResetOrder}
                    className="text-xs text-muted-foreground hover:text-foreground gap-1.5"
                    title="Reset all groups to alphabetical order"
                    data-testid={`${testIdPrefix}-reset-order`}
                  >
                    <ArrowDownAZ className="w-3.5 h-3.5" />
                    Reset A→Z
                  </Button>
                )}
              </div>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto pr-1 -mr-1" data-testid={`${testIdPrefix}-list-scroll`}>
              {loading ? (
                <div className="py-10 text-center text-sm text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
                </div>
              ) : groups.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>
                  No groups yet. Create your first group above.
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                  onDragEnd={onDragEnd}
                >
                  <SortableContext items={groups.map((g) => g.id)} strategy={verticalListSortingStrategy}>
                    <ul className="space-y-2">
                      {groups.map((group) => (
                        <SortableGroupRow
                          key={group.id}
                          group={group}
                          testIdPrefix={testIdPrefix}
                          editingGroup={editingGroup}
                          editName={editName}
                          setEditName={setEditName}
                          editColor={editColor}
                          setEditColor={setEditColor}
                          saveRename={saveRename}
                          cancelRename={cancelRename}
                          startRename={startRename}
                          openAllocate={openAllocate}
                          handleDelete={handleDelete}
                          itemLabelPlural={itemLabelPlural}
                        />
                      ))}
                    </ul>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col min-h-0 flex-1 gap-3">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={backToList} data-testid={`${testIdPrefix}-allocate-back`}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  value={allocateSearch}
                  onChange={(e) => setAllocateSearch(e.target.value)}
                  placeholder={`Search ${itemLabelPlural}…`}
                  className="pl-7"
                  data-testid={`${testIdPrefix}-allocate-search`}
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto pr-1 -mr-1" data-testid={`${testIdPrefix}-allocate-scroll`}>
              {filteredItems.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground">No {itemLabelPlural} match your search.</div>
              ) : (
                <ul className="space-y-1.5">
                  {filteredItems.map((it) => {
                    const itemId = it[itemIdField];
                    const isChecked = selectedItemIds.has(itemId);
                    const otherGroup = otherGroupByItem[itemId];
                    return (
                      <li
                        key={itemId}
                        className="flex items-center gap-2 rounded-md border border-border bg-background p-2 cursor-pointer hover:bg-accent"
                        onClick={() => toggleItem(itemId)}
                        data-testid={`${testIdPrefix}-allocate-row-${itemId}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleItem(itemId)}
                          className="h-4 w-4 accent-[#2563eb] shrink-0"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`${testIdPrefix}-allocate-checkbox-${itemId}`}
                          aria-label={`Toggle ${itemLabel(it)}`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{itemLabel(it)}</p>
                          {itemSubLabel && <p className="text-[10px] text-muted-foreground truncate">{itemSubLabel(it)}</p>}
                        </div>
                        {otherGroup && !isChecked && (
                          <Badge variant="outline" className="text-[10px] shrink-0">in “{otherGroup}”</Badge>
                        )}
                        {otherGroup && isChecked && (
                          <Badge variant="outline" className="text-[10px] shrink-0 border-amber-400 text-amber-600">moving from “{otherGroup}”</Badge>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground">{selectedItemIds.size} selected</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={backToList} disabled={savingAllocate}>Cancel</Button>
                <Button onClick={handleSaveAllocate} disabled={savingAllocate} data-testid={`${testIdPrefix}-allocate-save`}>
                  {savingAllocate ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        )}

        <AlertDialog open={confirmResetOpen} onOpenChange={setConfirmResetOpen}>
          <AlertDialogContent data-testid={`${testIdPrefix}-reset-order-confirm`}>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset group order?</AlertDialogTitle>
              <AlertDialogDescription>
                This resets all groups in this list to alphabetical (A→Z) order and clears your custom drag-and-drop ordering.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`${testIdPrefix}-reset-order-cancel`}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={doResetOrder} data-testid={`${testIdPrefix}-reset-order-confirm-btn`}>
                Reset to A→Z
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!confirmDeleteGroup} onOpenChange={(o) => { if (!o) setConfirmDeleteGroup(null); }}>
          <AlertDialogContent data-testid={`${testIdPrefix}-delete-confirm`}>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete group “{confirmDeleteGroup?.name}”?</AlertDialogTitle>
              <AlertDialogDescription>
                Items in this group will become Ungrouped (for you only — other accounts are not affected). This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`${testIdPrefix}-delete-cancel`}>Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={doDelete} data-testid={`${testIdPrefix}-delete-confirm-btn`}>
                Delete group
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};

export default GroupsManagerModal;
